import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { eur } from "@/data/ambassadorProgramme";
import { CommissionsPanel, PayoutsPanel, FraudPanel, ReportsPanel } from "@/components/admin/AmbassadorFinance";

const SUBTABS = [["list", "Ambassadors"], ["commissions", "Commissions"], ["payouts", "Wise payouts"], ["fraud", "Fraud alerts"], ["reports", "Reports"], ["settings", "Discount links"]];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

const Detail = ({ a }) => (
  <div className="text-xs text-zinc-700 grid sm:grid-cols-2 gap-2 p-3 bg-[var(--muted-surface)] rounded-xl mt-2" data-testid={`amb-detail-${a.id}`}>
    <div><b>Audience:</b> {a.audience}</div><div><b>Motivation:</b> {a.motivation}</div>
    <div><b>Channels:</b> {(a.channels || []).join(", ") || "-"}</div><div><b>Links:</b> {a.linkedin || "-"} {a.website}</div>
    <div><b>Phone:</b> {a.phone || "-"} · <b>Terms v{a.terms_version}</b> accepted {a.terms_accepted_at?.slice(0, 10)}</div>
    <div><b>Wise:</b> {a.payout_details?.account_holder ? `${a.payout_details.account_holder} · ${a.payout_details.iban || a.payout_details.account_number || a.payout_details.wise_email} · ${a.payout_details.currency}` : "Not provided"}</div>
  </div>
);

const AmbassadorList = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/ambassadors").then((r) => setRows(r.data)).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (a, status) => {
    const note = ["rejected", "suspended", "terminated"].includes(status) ? window.prompt(`Reason to include in the ${status} email (optional)`) ?? null : "";
    if (note === null) return;
    try { await api.post(`/admin/ambassadors/${a.id}/status`, { status, note }); toast.success(`Ambassador ${status}`); load(); } catch (e) { err(e); }
  };
  const editCode = async (a) => {
    const code = window.prompt("New code (CSI-XXXX)", a.code);
    if (!code) return;
    try { await api.post(`/admin/ambassadors/${a.id}/code`, { code: code.toUpperCase() }); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-2" data-testid="admin-ambassador-list">
      {rows.length === 0 && <div className="text-sm text-zinc-500">No applications yet.</div>}
      {rows.map((a) => (
        <div key={a.id} className="card-warm p-4" data-testid={`amb-row-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <button type="button" className="text-left min-w-0" onClick={() => setOpen(open === a.id ? null : a.id)}>
              <div className="font-semibold">{a.name} <span className="text-xs text-zinc-500">{a.email} · {a.country}</span></div>
              <div className="text-xs text-zinc-600">{a.code ? `${a.ambassador_id} · ${a.code}` : "No code yet"} · <span className="capitalize font-semibold" data-testid={`amb-status-${a.id}`}>{a.status}</span> · applied {a.created_at.slice(0, 10)}
                {a.stats && ` · ${a.stats.enrolments} enrolments · ${eur(a.stats.revenue)} revenue · ${a.stats.level}`}</div>
            </button>
            <div className="flex flex-wrap gap-1">
              {a.status !== "active" && a.status !== "terminated" && <button className="chip text-xs bg-emerald-50" onClick={() => setStatus(a, "active")} data-testid={`amb-approve-${a.id}`}>{a.status === "suspended" ? "Reactivate" : "Approve"}</button>}
              {a.status === "pending" && <button className="chip text-xs" onClick={() => setStatus(a, "rejected")} data-testid={`amb-reject-${a.id}`}>Reject</button>}
              {a.status === "active" && <button className="chip text-xs" onClick={() => setStatus(a, "suspended")} data-testid={`amb-suspend-${a.id}`}>Suspend</button>}
              {["active", "suspended"].includes(a.status) && <button className="chip text-xs text-red-700" onClick={() => setStatus(a, "terminated")} data-testid={`amb-terminate-${a.id}`}>Terminate</button>}
              {a.code && <button className="chip text-xs" onClick={() => editCode(a)} data-testid={`amb-code-${a.id}`}>Edit code</button>}
            </div>
          </div>
          {open === a.id && <Detail a={a}/>}
        </div>
      ))}
    </div>
  );
};

const DiscountLinks = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/ambassador-settings").then((r) => setRows(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const save = async (r, url) => {
    try { await api.patch(`/admin/courses/${r.id}/discount-link`, { discount_payment_link_url: url }); toast.success("Saved"); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3" data-testid="admin-discount-links">
      <p className="text-sm text-zinc-600">Referred learners are sent to these SumUp links (5% off). If a link is empty, codes are not accepted for that programme.</p>
      {rows.map((r) => (
        <form key={r.id} onSubmit={(e) => { e.preventDefault(); save(r, e.target.url.value); }} className="card-warm p-4 flex flex-wrap items-center gap-3">
          <div className="min-w-[200px]"><b>{r.code}</b><div className="text-xs text-zinc-500">{eur(r.price)} → {eur(r.discounted_price)}</div></div>
          <input name="url" defaultValue={r.discount_payment_link_url || ""} className="field flex-1 min-w-[220px] text-sm" placeholder="https://pay.sumup.com/…" data-testid={`discount-link-${r.code}`}/>
          <button className="chip text-xs" data-testid={`discount-link-save-${r.code}`}>Save</button>
        </form>
      ))}
    </div>
  );
};

export const AmbassadorsTab = () => {
  const [sub, setSub] = useState("list");
  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/ambassador-export.xlsx", { responseType: "blob" });
      Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: `CSI-Ambassadors-${new Date().toISOString().slice(0, 10)}.xlsx` }).click();
    } catch (e) { toast.error("Export failed"); }
  };
  return (
    <div data-testid="admin-ambassadors-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {SUBTABS.map(([k, l]) => <button key={k} onClick={() => setSub(k)} className={`chip text-xs ${sub === k ? "bg-[var(--navy)] text-white border-[var(--navy)]" : ""}`} data-testid={`amb-subtab-${k}`}>{l}</button>)}
        <button onClick={exportXlsx} className="chip text-xs ml-auto inline-flex items-center gap-1" data-testid="amb-export-xlsx"><FileSpreadsheet size={12}/> Export to Excel</button>
      </div>
      {sub === "list" && <AmbassadorList/>}
      {sub === "commissions" && <CommissionsPanel/>}
      {sub === "payouts" && <PayoutsPanel/>}
      {sub === "fraud" && <FraudPanel/>}
      {sub === "reports" && <ReportsPanel/>}
      {sub === "settings" && <DiscountLinks/>}
    </div>
  );
};
